import React from "react";
import { Navbar } from "@/components/Navbar";
import { useLang } from "@/lib/i18n";
import { useAuth } from "@/contexts/AuthContext";
import { Trophy, Target, TrendingUp, Award, Lock } from "lucide-react";
import { computeBadge, levelProgress, titleForLevel, allTitles, BADGE_STYLES, BADGE_RING } from "@/lib/levels";
import { Cashier } from "@/components/Cashier";
import { ChangePassword } from "@/components/ChangePassword";
import { ExecutionHistory } from "@/components/ExecutionHistory";
import { EmailVerifyBanner } from "@/components/EmailVerifyBanner";

function fmt2(n) {
  return Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function Profile() {
  const { t } = useLang();
  const { user } = useAuth();
  if (!user) return null;
  const s = user.stats || { matches: 0, wins: 0, best_profit: 0, total_profit: 0 };
  const winrate = s.matches ? Math.round((s.wins / s.matches) * 100) : 0;
  const badge = computeBadge(s);
  const prog = levelProgress(s);
  const title = titleForLevel(prog.level);

  const cards = [
    { icon: Target, label: t("stat_matches"), value: s.matches, color: "text-white" },
    { icon: Trophy, label: t("stat_wins"), value: `${s.wins} · ${winrate}%`, color: "text-signal" },
    { icon: Award, label: t("stat_best"), value: `${s.best_profit >= 0 ? "+" : ""}${fmt2(s.best_profit)}`, color: s.best_profit >= 0 ? "text-profit" : "text-loss" },
    { icon: TrendingUp, label: t("stat_total"), value: `${s.total_profit >= 0 ? "+" : ""}${fmt2(s.total_profit)}`, color: s.total_profit >= 0 ? "text-profit" : "text-loss" },
  ];

  return (
    <div className="min-h-screen bg-base text-white">
      <Navbar />
      <EmailVerifyBanner />
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-14">
        <div className="flex items-center gap-5 mb-10">
          <div className="relative">
            <div className={`w-20 h-20 rounded-full bg-panel flex items-center justify-center font-heading font-black text-3xl text-signal ${BADGE_RING[badge]}`}>
              {((user.name || user.email || "?")[0] || "?").toUpperCase()}
            </div>
            <span className="absolute -bottom-1 -right-1 bg-base border border-signal/50 text-signal font-mono text-[10px] font-bold px-1.5 py-0.5 rounded" data-testid="profile-level">
              Lv {prog.level}
            </span>
          </div>
          <div>
            <h1 className="font-heading font-black text-3xl tracking-tight">{user.name || t("profile_title")}</h1>
            <div className="flex items-center gap-2 mt-1">
              <span className="font-mono text-sm text-signal" data-testid="profile-title">{title}</span>
              <span className={`font-mono text-[10px] uppercase tracking-widest border px-2 py-0.5 ${BADGE_STYLES[badge]}`} data-testid="profile-badge">{badge}</span>
            </div>
            <div className="font-mono text-xs text-white/40 mt-1">{user.email}</div>
          </div>
        </div>

        {/* level progress */}
        <div className="border border-white/10 bg-panel p-5 mb-8">
          <div className="flex items-center justify-between font-mono text-xs text-white/50 mb-2">
            <span>Lv {prog.level}</span>
            <span data-testid="profile-tonext">{prog.toNext} XP {t("to_next_level")}</span>
            <span>Lv {prog.level + 1}</span>
          </div>
          <div className="h-2 bg-white/10 overflow-hidden rounded-full">
            <div className="h-full bg-signal rounded-full transition-[width] duration-500" style={{ width: `${prog.pct}%` }} />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 mb-10">
          {cards.map((c, i) => (
            <div key={c.label} data-testid={`stat-${i}`} className="bg-panel p-7 hover:bg-panelhover transition-colors">
              <c.icon className="w-6 h-6 text-white/30 mb-5" strokeWidth={1.75} />
              <div className={`font-mono font-bold text-2xl tabular-nums ${c.color}`}>{c.value}</div>
              <div className="font-mono text-xs uppercase tracking-widest text-white/40 mt-1">{c.label}</div>
            </div>
          ))}
        </div>

        {/* rewards */}
        <h2 className="font-heading font-bold text-xl mb-4 flex items-center gap-2">
          <Award className="w-5 h-5 text-signal" /> {t("rewards")}
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-px bg-white/10 border border-white/10">
          {allTitles().map((r) => {
            const unlocked = prog.level >= r.min;
            return (
              <div
                key={r.name}
                data-testid={`reward-${r.name}`}
                className={`bg-panel p-4 text-center ${unlocked ? "" : "opacity-40"}`}
              >
                <div className={`font-mono text-sm font-bold ${unlocked ? "text-signal" : "text-white/50"}`}>{r.name}</div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-white/40 mt-1 flex items-center justify-center gap-1">
                  {unlocked ? `Lv ${r.min}` : <><Lock className="w-3 h-3" /> Lv {r.min}</>}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-12">
          <Cashier />
        </div>

        <div className="mt-12">
          <ExecutionHistory />
        </div>

        <div className="mt-12">
          <ChangePassword />
        </div>
      </div>
    </div>
  );
}
